const attackTypeLabel = (attackType) => {
    if (attackType === null || attackType === undefined) return null;
    switch (attackType) {
        case 1:
            return 'Melee';
        case 2:
            return 'Ranged';
        case 3:
            return 'Spell';
        default:
            return String(attackType);
    }
};

const getCategoryLabel = (categoryId) => {
    if (categoryId === null || categoryId === undefined) return null;
    switch (categoryId) {
        case 1:
            return 'Simple';
        case 2:
            return 'Martial';
        default:
            return String(categoryId);
    }
};

const removeHtmlTags = (html = '') => {
    if (typeof document !== 'undefined') {
        const temp = document.createElement('div');
        temp.innerHTML = html;
        return temp.textContent || temp.innerText || '';
    }

    return html
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/p>/gi, '\n')
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/gi, ' ')
        .replace(/&amp;/gi, '&')
        .replace(/&lt;/gi, '<')
        .replace(/&gt;/gi, '>')
        .replace(/&quot;/gi, '"')
        .replace(/&#39;/gi, "'")
        .replace(/\s+/g, ' ')
        .trim();
};

const formatDamage = (damage) => {
    if (!damage) return null;
    if (damage.diceString) return damage.diceString;
    const parts = [];
    if (damage.diceCount && damage.diceValue) {
        parts.push(`${damage.diceCount}d${damage.diceValue}`);
    }
    if (damage.fixedValue || damage.fixedValue === 0) {
        parts.push(`${damage.fixedValue}`);
    }
    return parts.join(' + ');
};

const formatRange = (range, longRange) => {
    if (range == null && longRange == null) return null;
    if (range != null && longRange != null && range === longRange) {
        return `${range}`;
    }
    if (range != null && longRange != null) {
        return `${range}/${longRange}`;
    }
    return range != null ? `${range}` : `${longRange}`;
};

const skillMod = (skills, index) => {
    return Math.floor((skills[index]["total score"]-10)/2)
}

function getMod(skill_json_array, modString) {
    if(modString === null) return 0;
    
    if(modString === "STR") {
        return skillMod(skill_json_array, 0);
    } else if(modString === "DEX") {
        return skillMod(skill_json_array, 1);
    } else if(modString === "BOTH") {
        let STR = skillMod(skill_json_array, 0);
        let DEX = skillMod(skill_json_array, 1);
        if(STR > DEX) {
            return skillMod(skill_json_array, 0);
        } else {
            return skillMod(skill_json_array, 1);
        }
    }

    return 0;
}

const addSign = (value) => {
    return value >= 0 ? `+${value}` : `${value}`;
};

const buildEntrySummary = (item, skills_json_array, simpleWeaponProf, martialWeaponProf, profBonus) => {
    const parts = [];

    if (item.type && item.type !== item.name) parts.push(item.type);

    const attackLabel = attackTypeLabel(item.attackType);
    const categoryLabel = getCategoryLabel(item.categoryId);

    let modString = null;
    if(attackLabel === "Melee") {
        // Melee and finesse
        if(item.properties.filter(p => p.name === "Finesse").length > 0) {
            modString = "BOTH" // STR or DEX
        } else { // Just melee
            modString = "STR"
        }

    // Ranged like Bows and crossbows
    } else if (attackLabel === "Ranged") {
        modString = "DEX"
    }

    let toHitMod = 0;
    // If simple weapon, check for martial or simple proficiency
    if(categoryLabel === "Simple") {
        if(simpleWeaponProf === "proficiency" || martialWeaponProf === "proficiency")
            toHitMod = profBonus;
        else if(simpleWeaponProf === "expertise" || martialWeaponProf === "expertise")
            toHitMod = profBonus * 2;
    // if martial weapon only check for martial proficiency
    } else if(categoryLabel === "Martial") {
        if(martialWeaponProf === "proficiency")
            toHitMod = profBonus;
        else if(martialWeaponProf === "expertise")
            toHitMod = profBonus * 2;
    }

    let mod = getMod(skills_json_array, modString)
    toHitMod = toHitMod + mod; // Ensure non-negative modifier

    let damageText = formatDamage(item.damage);
    if (damageText) {
        damageText = `{@hit ${toHitMod}} to hit, ({@damage ` + damageText + `${addSign(mod)}})`

        parts.push(`${damageText}${item.damageType ? ` ${item.damageType}` : ''}`.trim());
    } else if (item.damageType) {
        parts.push(`${item.damageType}`);
    }

    const rangeText = formatRange(item.range, item.longRange);
    if (rangeText) parts.push(`Range: ${rangeText}`);

    if (Array.isArray(item.properties) && item.properties.length > 0) {
        const propNames = item.properties.map((prop) => prop.name).filter(Boolean);
        if (propNames.length) parts.push(`Properties: ${propNames.join(', ')}`);
    }

    if (Array.isArray(item.grantedModifiers) && item.grantedModifiers.length > 0) {
        const mods = item.grantedModifiers
            .map((mod) => {
                const value = mod.fixedValue != null ? `${addSign(mod.fixedValue)}` : mod.dice || '';
                return [value, mod.type, mod.subType].filter(Boolean).join(' ');
            })
            .filter(Boolean);
        if (mods.length) parts.push(`Modifiers: ${mods.join(', ')}`);
    }

    return `${parts.join(' ')}`;
};

export const mapDndBItemsToLairActions = (items = [], skills_json_array, proficiencies, profBonus) => {
    let simpleWeaponProf = proficiencies['simple-weapons']; // proficiency, expertise or undefined
    let martialWeaponProf = proficiencies['martial-weapons']; // proficiency, expertise or undefined
    if (!Array.isArray(items)) return [];

    return items.map((item) => {
        const name = item?.name || 'Unnamed item';
        const description = removeHtmlTags(item?.description || '');
        const summary = buildEntrySummary(item, skills_json_array, simpleWeaponProf, martialWeaponProf, profBonus);

        let entryParts = null;

        if(item.filterType === "Weapon") {
            entryParts = [summary].filter(Boolean);
        } else {
            entryParts = [summary, description].filter(Boolean);
        }

        const entry = entryParts.join('. ');

        return {
            type: 'item',
            name,
            entry,
            rechargeCount: 0,
        };
    });
};
